// Late payments (roadmap Phase 1.6): finance charges and overdue reminders. No Firebase or DOM, so
// tests/unit and the server (functions/collections.js, via functions/shared/) run the same rules.
//
// Settings › Pricing › Invoices & late payments, saved at /billingTerms (admin write):
//   dueDays           days from issue to due (30)
//   financeRatePct    monthly finance charge on past-due balances, in percent (1.5; 0 = none)
//   graceDays         days past due before a finance charge applies (0)
//   financeMode       off | manual (the office adds them from Invoices › Accounts) | auto (the 1st
//                     of each month, by the server)
//   remindersAuto     true: the server emails overdue reminders each morning on the schedule below
//   reminderDays      "7, 30, 60": days past due when a reminder goes out (each one once per invoice)
//   reminderSubject / reminderBody   the email, with {first_name} {name} {marina} {overdue_total}
//                     {invoice_list} {oldest_days}
//
// Finance charges are their own invoices (source 'finance', due on receipt): one per customer per
// month, a line per past-due invoice. Never charged on a finance-charge invoice, never twice on one
// invoice in one month (the server also claims /invoiceIndex/finance/{invoiceId}_{YYYY-MM}).

import { roundCents, formatMoney } from './money.js';

export const TERMS_DEFAULTS = Object.freeze({
  dueDays: 30,
  financeRatePct: 1.5,
  graceDays: 0,
  financeMode: 'off',
  remindersAuto: false,
  reminderDays: '7, 30, 60',
  reminderSubject: 'Payment reminder from {marina}',
  reminderBody: 'Hi {first_name},\n\nOur records show {overdue_total} past due on your account:\n\n{invoice_list}\n\n'
    + 'If you have already sent payment, thank you, and please disregard this reminder. If not, please reply or give us a call to arrange payment.\n\n'
    + 'Thank you,\n{marina}',
});

export const FINANCE_MODES = ['off', 'manual', 'auto'];

const int = (v, lo, hi, dflt) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : dflt;
};

/** "7, 30, 60" → [7, 30, 60] (sorted, no repeats, 1–365). */
export function parseStages(v) {
  const list = (Array.isArray(v) ? v : String(v || '').split(/[,\s;]+/)).map(Number)
    .filter(n => Number.isInteger(n) && n >= 1 && n <= 365);
  return [...new Set(list)].sort((a, b) => a - b);
}

/** Saved settings (or nothing) → complete, safe settings. */
export function billingTerms(raw) {
  const r = { ...TERMS_DEFAULTS, ...(raw && typeof raw === 'object' ? raw : {}) };
  const rate = Number(r.financeRatePct);
  return {
    dueDays: int(r.dueDays, 0, 365, TERMS_DEFAULTS.dueDays),
    financeRatePct: Number.isFinite(rate) ? Math.min(5, Math.max(0, Math.round(rate * 1000) / 1000)) : TERMS_DEFAULTS.financeRatePct,
    graceDays: int(r.graceDays, 0, 365, 0),
    financeMode: FINANCE_MODES.includes(r.financeMode) ? r.financeMode : 'off',
    remindersAuto: r.remindersAuto === true,
    reminderDays: parseStages(r.reminderDays).join(', '),
    stages: parseStages(r.reminderDays),
    reminderSubject: String(r.reminderSubject || TERMS_DEFAULTS.reminderSubject).slice(0, 200),
    reminderBody: String(r.reminderBody || TERMS_DEFAULTS.reminderBody).slice(0, 4000),
  };
}

export const financeOn = t => t.financeMode !== 'off' && t.financeRatePct > 0;

/** The sentence for invoices and statements, or '' when there are no finance charges. */
export function termsLine(raw) {
  const t = billingTerms(raw);
  if (!financeOn(t)) return '';
  return `A ${t.financeRatePct}% monthly finance charge applies to balances ${t.graceDays ? `more than ${t.graceDays} days ` : ''}past due.`;
}

export const daysLate = (dueDate, today) => (dueDate ? Math.round((Date.parse(today) - Date.parse(dueDate)) / 86400000) : 0);
export const periodOf = today => String(today).slice(0, 7);
const isOpen = inv => inv && (inv.status === 'sent' || inv.status === 'partial') && inv.balanceCents > 0 && inv.number;

export function fmtDay(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
export function monthLabel(period) {
  const [y, m] = String(period).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * The finance charges due today: [{ customerId, name, lines: [{ invoiceId, number, pastDueCents,
 * daysLate, chargeCents, description }], totalCents }], biggest first. [] when they're off.
 */
export function financeCharges(invoices, rawTerms, today) {
  const t = billingTerms(rawTerms);
  if (!(t.financeRatePct > 0)) return [];
  const period = periodOf(today);
  const out = new Map();
  Object.values(invoices || {}).forEach(inv => {
    if (!isOpen(inv) || inv.source === 'finance' || !inv.dueDate || !inv.customerId) return;
    const late = daysLate(inv.dueDate, today);
    if (late <= t.graceDays || inv.financeCharges?.[period]) return;
    const chargeCents = roundCents(inv.balanceCents * t.financeRatePct / 100);
    if (chargeCents < 1) return;
    if (!out.has(inv.customerId)) out.set(inv.customerId, { customerId: inv.customerId, name: inv.customerName || 'Customer', lines: [], totalCents: 0 });
    const c = out.get(inv.customerId);
    c.lines.push({
      invoiceId: inv.id, number: inv.number, pastDueCents: inv.balanceCents, daysLate: late, chargeCents,
      description: `Finance charge ${t.financeRatePct}% on ${inv.number} (${formatMoney(inv.balanceCents)} past due since ${fmtDay(inv.dueDate)})`,
    });
    c.totalCents += chargeCents;
  });
  return [...out.values()].sort((a, b) => b.totalCents - a.totalCents || a.name.localeCompare(b.name));
}

/** Customers with anything past due → Map customerId → { customerId, name, email, invoices[], overdueCents, oldestDays }. */
export function overdueByCustomer(invoices, today) {
  const out = new Map();
  Object.values(invoices || {}).forEach(inv => {
    if (!isOpen(inv) || !inv.customerId) return;
    const late = daysLate(inv.dueDate, today);
    if (late <= 0) return;
    if (!out.has(inv.customerId)) out.set(inv.customerId, { customerId: inv.customerId, name: inv.customerName || 'Customer', email: inv.customerEmail || '', invoices: [], overdueCents: 0, oldestDays: 0 });
    const c = out.get(inv.customerId);
    if (!c.email && inv.customerEmail) c.email = inv.customerEmail;
    c.invoices.push({ id: inv.id, number: inv.number, dueDate: inv.dueDate, balanceCents: inv.balanceCents, daysLate: late, remindersSent: inv.remindersSent || null, lastReminderAt: inv.lastReminderAt || null });
    c.overdueCents += inv.balanceCents;
    c.oldestDays = Math.max(c.oldestDays, late);
  });
  out.forEach(c => c.invoices.sort((a, b) => b.daysLate - a.daysLate));
  return out;
}

/** The latest reminder step an invoice has reached (e.g. 30 for 45 days late with [7, 30, 60]), or 0. */
export function stageFor(late, stages) {
  return stages.filter(s => s <= late).pop() || 0;
}

/** Customers the automatic reminder should email today: an invoice has reached a step not yet sent. */
export function remindersDue(invoices, rawTerms, today) {
  const t = billingTerms(rawTerms);
  if (!t.stages.length) return [];
  return [...overdueByCustomer(invoices, today).values()].filter(c => c.invoices.some(i => {
    const s = stageFor(i.daysLate, t.stages);
    return s && !i.remindersSent?.[s];
  }));
}

/** The reminder email for one customer: { subject, text }. */
export function reminderMessage(c, rawTerms, marina) {
  const t = billingTerms(rawTerms);
  const vars = {
    first_name: String(c.name || '').split(' ')[0] || 'there',
    name: c.name || '',
    marina: marina || 'the marina',
    overdue_total: formatMoney(c.overdueCents),
    oldest_days: String(c.oldestDays),
    invoice_list: c.invoices.map(i => `• ${i.number}: ${formatMoney(i.balanceCents)}, due ${fmtDay(i.dueDate)} (${i.daysLate} day${i.daysLate === 1 ? '' : 's'} past due)`).join('\n'),
  };
  const fill = s => String(s).replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
  return { subject: fill(t.reminderSubject).replace(/[\r\n]+/g, ' ').trim(), text: fill(t.reminderBody) };
}
